import { createHook, createSelector, type BoundActions } from '@atlassian/react-sweet-state';
import type { State, ThirdPartyEntity, ThirdPartyState } from '../../../../common/types';
import type { Actions } from '../../actions';
import { Store } from '../../main';

export type ExtendedThirdPartyState = ThirdPartyState & {
	selectedEntity: ThirdPartyEntity | null;
};

export type UseThirdPartyState = () => [ExtendedThirdPartyState, BoundActions<State, Actions>];

export const thirdPartyStateSelector = createSelector(
	(state: State): ThirdPartyState => state.thirdPartyState,
	(newState: ThirdPartyState) => newState,
);

const useThirdPartyStateInner = createHook(Store, {
	selector: thirdPartyStateSelector,
});

export type UseThirdPartySelectedEntity = () => [
	ThirdPartyEntity | null,
	Partial<BoundActions<State, Actions>>,
];

export const thirdPartySelectedEntitySelector = createSelector(
	(state: State): string | null => state.thirdPartyState.selectedEntityId,
	(state: State): ThirdPartyEntity[] | null => state.thirdPartyState.selectedEntities,
	(
		selectedEntityId: string | null,
		selectedEntities: ThirdPartyEntity[] | null,
	): ThirdPartyEntity | null => {
		if (
			selectedEntityId &&
			selectedEntities &&
			selectedEntities.length === 1 &&
			selectedEntities[0].id === selectedEntityId
		) {
			return selectedEntities[0];
		}
		return null;
	},
);

const useThirdPartySelectedEntity = createHook(Store, {
	selector: thirdPartySelectedEntitySelector,
});

export const useThirdPartyState: UseThirdPartyState = () => {
	const [thirdPartyState, actions] = useThirdPartyStateInner();
	const [selectedEntity] = useThirdPartySelectedEntity();
	const extendedThirdPartyState = {
		...thirdPartyState,
		selectedEntity,
	};
	return [extendedThirdPartyState, actions];
};
